import React, { useMemo } from 'react';
import { Modal, Button, Row, Col, Badge, Alert, Spinner } from 'react-bootstrap';
import ViewPdfButton from './ViewPdfButton';

const badgeVariant = (status) => ({ PENDING: 'warning', CONFIRMED: 'info', NOT_CONFIRMED: 'secondary', PAID: 'success' }[status] || 'dark');
const round2 = (n) => Number((Number(n) || 0).toFixed(2));

export default function ZelleViewModal({ show, onHide, zelle, loading = false, error = '' }) {
  const rate = Number(zelle?.rateUsdHtg || 0);
  const amountUSD = Number(zelle?.amountUSD || 0);
  const feeUSD = Number(zelle?.feeUSD || 0);
  const netUSD = round2(Math.max(0, amountUSD - feeUSD));
  const clientName = useMemo(() => zelle?.client
    ? `${zelle.client.firstName || ''} ${zelle.client.lastName || ''}`.trim()
    : 'Non-client', [zelle]);

  const getDocument = () => ({
    title: `Détails transaction Zelle${zelle?.confirmationCode ? ` - ${zelle.confirmationCode}` : ''}`,
    sections: [
      { title: 'Sender', rows: [
        { label: 'Nom', value: zelle?.senderName || '—' },
        { label: 'WhatsApp', value: zelle?.senderWhatsapp || '—' },
        { label: 'Téléphone', value: zelle?.senderPhone || '—' },
      ] },
      { title: 'Receiver', rows: [
        { label: 'Nom', value: zelle?.beneficiaryName || '—' },
        { label: 'Client', value: clientName },
        { label: 'WhatsApp', value: zelle?.receiverWhatsapp || '—' },
        { label: 'Téléphone', value: zelle?.receiverPhone || '—' },
      ] },
      { title: 'Transaction', rows: [
        { label: 'Date transaction', value: zelle?.transactionDate || '—' },
        { label: 'Code confirmation', value: zelle?.confirmationCode || '—' },
        { label: 'Motif', value: zelle?.transactionReason || '—' },
        { label: 'Statut', value: zelle?.status || '—' },
        { label: 'Montant reçu', value: zelle?.amountUSD == null ? '—' : `${amountUSD.toFixed(2)} USD` },
        { label: 'Frais', value: `${feeUSD.toFixed(2)} USD` },
        { label: 'Net à remettre', value: zelle?.amountUSD == null ? '—' : `${netUSD.toFixed(2)} USD` },
        { label: 'Taux', value: rate > 0 ? `${rate.toFixed(2)} HTG` : '—' },
        { label: 'Net HTG', value: zelle?.amountHTG == null ? '—' : `${Number(zelle.amountHTG).toFixed(2)} HTG` },
      ] },
      { title: 'Suivi', rows: [
        { label: 'Créée le', value: zelle?.createdAt ? new Date(zelle.createdAt).toLocaleString() : '—' },
        { label: 'Créée par', value: zelle?.createdByUser?.username || '—' },
        { label: 'Confirmée le', value: zelle?.confirmedAt ? new Date(zelle.confirmedAt).toLocaleString() : '—' },
        { label: 'Confirmée par', value: zelle?.confirmedByUser?.username || '—' },
        { label: 'Payée le', value: zelle?.paidAt ? new Date(zelle.paidAt).toLocaleString() : '—' },
        { label: 'Payée par', value: zelle?.paidByUser?.username || '—' },
        { label: 'Payé USD', value: `${Number(zelle?.paidUSD || 0).toFixed(2)} USD` },
        { label: 'Payé HTG', value: `${Number(zelle?.paidHTG || 0).toFixed(2)} HTG` },
      ] },
      { title: 'Notes', rows: [
        { label: 'Note transaction', value: zelle?.note || '—' },
        { label: 'Note paiement', value: zelle?.paymentNote || '—' },
      ] },
    ],
  });

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton><Modal.Title>Détails transaction Zelle</Modal.Title></Modal.Header>
      <Modal.Body>
        {loading && <Alert variant="secondary" className="d-flex align-items-center gap-2"><Spinner animation="border" size="sm" />Chargement des détails…</Alert>}
        {!!error && <Alert variant="danger">{error}</Alert>}
        {!loading && !zelle && !error && <Alert variant="secondary">Aucune donnée à afficher.</Alert>}
        {!!zelle && <>
          <Alert variant="secondary" className="d-flex justify-content-between align-items-center">
            <div><div className="fw-semibold">{zelle.senderName || 'Sender non renseigné'}</div><div className="text-muted small">Code : {zelle.confirmationCode || '—'}</div></div>
            <Badge bg={badgeVariant(zelle.status)}>{zelle.status || '—'}</Badge>
          </Alert>
          <Row className="g-3">
            <Col md={12}><h6>Sender</h6></Col>
            <Col md={4}><div className="text-muted small">Nom</div><div className="fw-semibold">{zelle.senderName || '—'}</div></Col>
            <Col md={4}><div className="text-muted small">WhatsApp</div><div>{zelle.senderWhatsapp || '—'}</div></Col>
            <Col md={4}><div className="text-muted small">Téléphone</div><div>{zelle.senderPhone || '—'}</div></Col>
            <Col md={12}><hr className="my-1" /><h6>Receiver</h6></Col>
            <Col md={4}><div className="text-muted small">Nom</div><div className="fw-semibold">{zelle.beneficiaryName || '—'}</div></Col>
            <Col md={4}><div className="text-muted small">Client</div><div>{clientName}</div></Col>
            <Col md={4}><div className="text-muted small">Contacts</div><div>{zelle.receiverWhatsapp || zelle.receiverPhone || '—'}</div></Col>
            <Col md={12}><hr className="my-1" /><h6>Transaction</h6></Col>
            <Col md={3}><div className="text-muted small">Date</div><div>{zelle.transactionDate || '—'}</div></Col>
            <Col md={3}><div className="text-muted small">Montant reçu</div><div>{zelle.amountUSD == null ? '—' : `${amountUSD.toFixed(2)} USD`}</div></Col>
            <Col md={3}><div className="text-muted small">Frais</div><div>{zelle.amountUSD == null ? '—' : `${feeUSD.toFixed(2)} USD`}</div></Col>
            <Col md={3}><div className="text-muted small">Net à remettre</div><div>{zelle.amountUSD == null ? '—' : `${netUSD.toFixed(2)} USD`}</div></Col>
            <Col md={12}><div className="text-muted small">Motif</div><div>{zelle.transactionReason || '—'}</div></Col>
            <Col md={6}><div className="text-muted small">Note transaction</div><div style={{ whiteSpace: 'pre-wrap' }}>{zelle.note || '—'}</div></Col>
            <Col md={6}><div className="text-muted small">Note paiement</div><div style={{ whiteSpace: 'pre-wrap' }}>{zelle.paymentNote || '—'}</div></Col>
          </Row>
        </>}
      </Modal.Body>
      <Modal.Footer>{!!zelle && <ViewPdfButton getDocument={getDocument} className="me-2" />}<Button variant="secondary" onClick={onHide}>Fermer</Button></Modal.Footer>
    </Modal>
  );
}
